import React, { useState } from 'react';
import PatientManualRegistrationMOUStep1, {
	PatientManualRegistrationMOUStep1Values,
} from './PatientManualRegistrationMOUStep1';
import PatientManualRegistrationMOUStep2, {
	PatientManualRegistrationMOUStep2Values,
} from './PatientManualRegistrationMOUStep2';
import apiClient from '../../../../../../Services/ApiClient';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import MediaViewerModal from '../../../../../../Components/MediaViewer/MediaViewerModal';
import { PatientManualRegistrationParams } from '../../PatientManualRegistration';
import useTranslation from '../../../../../../Hooks/useTranslation';
import useNotification from '../../../../../../Hooks/useNotification.hook';

interface Props {
	onSuccess: (params: PatientManualRegistrationParams) => void;
	number?: MouNumber;
	setIsLoading: (isLoading: boolean) => void;
}

export interface MouNumber {
	id?: string;
	number: string;
}

const PatientManualRegistrationMOU: React.FC<Props> = ({
	onSuccess,
	number,
	setIsLoading,
}) => {
	const { displayError } = useApiClient();
	const [mouNumber, setMouNumber] = useState<MouNumber | undefined>(number);
	const [file, setFile] = useState(undefined);
	const { t } = useTranslation();
	const { showNotificationSuccess } = useNotification();

	const handleStep1Success = async (
		values: PatientManualRegistrationMOUStep1Values,
	) => {
		setIsLoading(true);
		try {
			const result = await apiClient.post(`/user/number/`, {
				birth_number: values.birthNumber,
			});
			if (result && result.status === 200) {
				setMouNumber(result?.data);
			}
		} catch (e: any) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const handleStep2Success = async (
		values: PatientManualRegistrationMOUStep2Values,
	) => {
		setIsLoading(true);
		try {
			if (mouNumber) {
				if (values.emailCheck && values.email) {
					await apiClient.get(
						'/user/number/' + mouNumber.id + '/send-email/' + values.email,
						{},
					);
					showNotificationSuccess({
						content: t(
							'component.patientProfileMouNumber.codeHasBeenSendToPatientsEmail',
						),
					});
				}
				if (values.phoneCheck && values.phone) {
					await apiClient.get(
						'/user/number/' + mouNumber.id + '/send-sms/' + values.phone,
						{},
					);
					showNotificationSuccess({
						content: t(
							'component.patientProfileMouNumber.codeHasBeenSendToPatientsPhone',
						),
					});
				}
				if (values.printCheck) {
					const result: any = await apiClient.get(
						'/user/number/' + mouNumber.id + '/print/',
						{},
					);
					if (result) {
						setFile(result?.data?.url);
					}
				} else {
					onSuccess({
						patientId: mouNumber.number,
						parentId: null,
						withInvite: true,
						ignoreActions: true,
					});
				}
			}
		} catch (e: any) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const handleCloseAfterPrint = () => {
		setFile(undefined);
		onSuccess({
			patientId: mouNumber?.number ?? '',
			parentId: null,
			withInvite: true,
			ignoreActions: true,
		});
	};

	if (!mouNumber) {
		return (
			<>
				<PatientManualRegistrationMOUStep1 onSuccess={handleStep1Success} />
			</>
		);
	}

	return (
		<>
			<PatientManualRegistrationMOUStep2
				mouNumber={mouNumber.number}
				onSuccess={handleStep2Success}
			/>
			{file && (
				<MediaViewerModal
					opened={!!file}
					onClose={handleCloseAfterPrint}
					file={{
						id: '',
						download: file,
						preview: file,
						type: 'pdf',
						displayName: mouNumber.number ?? '',
					}}
				/>
			)}
		</>
	);
};

export default PatientManualRegistrationMOU;
